// Scene sections — the per-beat timeline of a scene (see `SceneSection`).
//
// A scene's `text` stays the canonical prose (AI edits, translations, reviews
// all work on it); `sections` add staging on top: who is on stage, their
// expressions, the location and extra cues for each reading beat. Everything
// here is pure so the player, the editor and the API stage scenes identically.
import { mvpLimits } from "./limits.js";
import {
  breakSeparator,
  segmentSceneTextDetailed,
  type SceneTextSegment
} from "./segmentText.js";
import type { Character, Novel, Scene, SceneSection } from "./types.js";

/** Whitespace-insensitive form of a piece of prose, for comparing texts. */
export function normalizeProse(text: string): string {
  return text.replace(/\s+/gu, " ").trim();
}

/** Joins sections back into prose, using each section's break separator. */
export function composeSectionsText(sections: readonly Pick<SceneSection, "text" | "break_before">[]): string {
  return sections
    .map((section, index) => (index === 0 ? section.text : breakSeparator(section.break_before) + section.text))
    .join("");
}

/** True when the sections spell out exactly the given prose (ignoring whitespace). */
export function sectionsMatchText(
  sections: readonly Pick<SceneSection, "text" | "break_before">[],
  text: string
): boolean {
  return normalizeProse(composeSectionsText(sections)) === normalizeProse(text);
}

/** A section id not yet used in `taken`. Deterministic for a given `taken` set. */
export function nextSectionId(taken: ReadonlySet<string>): string {
  let n = taken.size + 1;
  while (taken.has(`sec_${n}`)) n += 1;
  return `sec_${n}`;
}

// ─── Character mention detection (deterministic staging) ─────────────────────

interface MentionMatcher {
  id: string;
  /** Lowercase stems with the max word length each may match. */
  stems: { stem: string; maxLength: number }[];
}

/**
 * Stems for a character's name tokens, tolerant of inflection («Маша» →
 * «Машу», «Маше»): capitalised tokens of 3+ letters, trimmed by one letter
 * (4-letter tokens) or two (longer ones). Only capitalised words match, so a
 * lowercase common noun that shares a stem is never taken for the name.
 */
function mentionMatcher(character: Pick<Character, "id" | "name">): MentionMatcher {
  const tokens = character.name
    .split(/[^\p{L}\p{M}'’-]+/u)
    .filter((token) => token.length >= 3 && /^\p{Lu}/u.test(token));
  const stems = tokens.map((token) => {
    const cut = token.length <= 3 ? 0 : token.length === 4 ? 1 : 2;
    return { stem: token.slice(0, token.length - cut).toLowerCase(), maxLength: token.length + 3 };
  });
  return { id: character.id, stems };
}

/** Character ids named in `text`, in order of first mention. */
function mentionedCharacters(text: string, matchers: readonly MentionMatcher[]): string[] {
  const firstIndex = new Map<string, number>();
  for (const match of text.matchAll(/[\p{L}\p{M}'’-]+/gu)) {
    const word = match[0];
    if (!/^\p{Lu}/u.test(word)) continue;
    const lower = word.toLowerCase();
    for (const matcher of matchers) {
      if (firstIndex.has(matcher.id)) continue;
      if (matcher.stems.some((s) => lower.startsWith(s.stem) && word.length <= s.maxLength)) {
        firstIndex.set(matcher.id, match.index ?? 0);
      }
    }
  }
  return [...firstIndex.entries()].sort((a, b) => a[1] - b[1]).map(([id]) => id);
}

/**
 * Who is on stage at each beat, from the prose alone. A character steps on
 * stage at the beat that first names them and stays there (pronouns carry the
 * focus) until a newly named character needs the slot — the one named longest
 * ago leaves. At most `max` at a time, and a character keeps its slot (left or
 * right) while on stage so busts don't jump sides between beats.
 *
 * `arriving` is who walks into the scene with the reader (see `arrivingCast`):
 * they are on stage from the first beat, as if named just before it, instead of
 * vanishing until the prose names them again. Otherwise beats before anyone is
 * named show only the location. When the prose never names any present
 * character and nobody arrives, the first `max` of the roster stay on stage for
 * the whole scene (the pre-timeline behaviour).
 */
export function autoStageCharacters(
  texts: readonly string[],
  roster: readonly Pick<Character, "id" | "name">[],
  max: number = mvpLimits.maxSectionCharacters,
  arriving: readonly string[] = []
): string[][] {
  const matchers = roster.map(mentionMatcher);
  const lastMention = new Map<string, number>();
  const rosterIds = new Set(roster.map((c) => c.id));
  let stage: string[] = [...new Set(arriving.filter((id) => rosterIds.has(id)))].slice(0, max);
  for (const id of stage) lastMention.set(id, -1);
  const frames: string[][] = [];

  texts.forEach((text, beat) => {
    const named = mentionedCharacters(text, matchers).slice(0, max);
    const next = [...stage];
    for (const id of named) {
      if (!next.includes(id)) {
        if (next.length < max) {
          next.push(id);
        } else {
          // Evict the on-stage character named longest ago that this beat doesn't name.
          let evict = -1;
          let oldest = Number.POSITIVE_INFINITY;
          next.forEach((candidate, slot) => {
            if (named.includes(candidate)) return;
            const seen = lastMention.get(candidate) ?? -1;
            if (seen < oldest) {
              oldest = seen;
              evict = slot;
            }
          });
          if (evict >= 0) next[evict] = id;
        }
      }
      lastMention.set(id, beat);
    }
    stage = next;
    frames.push([...stage]);
  });

  if (roster.length > 0 && frames.every((frame) => frame.length === 0)) {
    const fallback = roster.slice(0, max).map((c) => c.id);
    return frames.map(() => [...fallback]);
  }
  return frames;
}

// ─── Resolution ──────────────────────────────────────────────────────────────

/** The scene's roster as characters, in `characters_present` order. */
function sceneRoster(scene: Scene, characters: readonly Character[]): Character[] {
  const byId = new Map(characters.map((c) => [c.id, c]));
  return scene.characters_present
    .map((id) => byId.get(id))
    .filter((c): c is Character => Boolean(c));
}

/** Drop staging that no longer resolves: characters off the roster, extra slots, stale emotions. */
function sanitizeSection(section: SceneSection, roster: ReadonlySet<string>): SceneSection {
  const onStage = [...new Set(section.characters.filter((id) => roster.has(id)))].slice(
    0,
    mvpLimits.maxSectionCharacters
  );
  const unchanged =
    onStage.length === section.characters.length &&
    onStage.every((id, i) => id === section.characters[i]) &&
    Object.keys(section.emotions ?? {}).every((id) => onStage.includes(id));
  if (unchanged) return section;
  const next: SceneSection = { ...section, characters: onStage };
  if (section.emotions) {
    const emotions = Object.fromEntries(
      Object.entries(section.emotions).filter(([id]) => onStage.includes(id))
    );
    if (Object.keys(emotions).length > 0) next.emotions = emotions;
    else delete next.emotions;
  }
  return next;
}

/** Fresh auto-staged sections for prose segments; `arriving` starts on stage. */
export function autoStageSegments(
  segments: readonly SceneTextSegment[],
  roster: readonly Pick<Character, "id" | "name">[],
  arriving: readonly string[] = []
): SceneSection[] {
  const frames = autoStageCharacters(
    segments.map((s) => s.text),
    roster,
    mvpLimits.maxSectionCharacters,
    arriving
  );
  const taken = new Set<string>();
  return segments.map((segment, index) => {
    const id = nextSectionId(taken);
    taken.add(id);
    const section: SceneSection = { id, text: segment.text, characters: frames[index] ?? [] };
    if (segment.break_before) section.break_before = segment.break_before;
    return section;
  });
}

/**
 * Re-segment changed prose while keeping the author's staging. Two passes:
 * 1. a new segment whose text matches an old section's (whitespace-blind)
 *    takes that section's staging and id;
 * 2. an EDITED sentence — an unmatched segment sitting where unmatched old
 *    sections sit (between the same matched neighbours) — takes the staging of
 *    the old section in the same position, so fixing a typo keeps the frame's
 *    cues, emotions and cast.
 * Any segment still unpaired (new sentences) continues the previous beat's
 * cast and location.
 */
export function carrySectionsOver(
  previous: readonly SceneSection[],
  segments: readonly SceneTextSegment[]
): SceneSection[] {
  const used = new Set<number>();
  const matches: number[] = [];
  let cursor = 0;

  const findMatch = (text: string): number => {
    const wanted = normalizeProse(text);
    for (let i = cursor; i < previous.length; i += 1) {
      if (!used.has(i) && normalizeProse(previous[i].text) === wanted) return i;
    }
    for (let i = 0; i < cursor && i < previous.length; i += 1) {
      if (!used.has(i) && normalizeProse(previous[i].text) === wanted) return i;
    }
    return -1;
  };

  for (const segment of segments) {
    const match = findMatch(segment.text);
    if (match >= 0) {
      used.add(match);
      cursor = match + 1;
    }
    matches.push(match);
  }

  // Pass 2: pair each run of unmatched segments with the unused old sections
  // between the same matched neighbours, in order.
  for (let i = 0; i < segments.length; ) {
    if (matches[i] >= 0) {
      i += 1;
      continue;
    }
    let end = i;
    while (end < segments.length && matches[end] < 0) end += 1;
    const lower = i > 0 ? matches[i - 1] : -1;
    const upper = end < segments.length ? matches[end] : previous.length;
    const candidates: number[] = [];
    for (let k = lower + 1; k < upper; k += 1) if (!used.has(k)) candidates.push(k);
    for (let j = i; j < end && candidates.length > 0; j += 1) {
      const k = candidates.shift() as number;
      used.add(k);
      matches[j] = k;
    }
    i = end;
  }

  const taken = new Set<string>();
  const allIds = previous.map((p) => p.id);
  const result: SceneSection[] = [];
  segments.forEach((segment, index) => {
    const match = matches[index];
    let section: SceneSection;
    if (match >= 0) {
      section = { ...previous[match], text: segment.text };
      delete section.break_before;
      if (taken.has(section.id)) section.id = nextSectionId(new Set([...taken, ...allIds]));
    } else {
      const before = result[result.length - 1] ?? previous[0];
      section = {
        id: nextSectionId(new Set([...taken, ...allIds])),
        text: segment.text,
        characters: before ? [...before.characters] : []
      };
      if (before?.background_asset_id) section.background_asset_id = before.background_asset_id;
    }
    if (segment.break_before) section.break_before = segment.break_before;
    taken.add(section.id);
    result.push(section);
  });
  return result;
}

/**
 * Sections for a scene that has never been staged (a novel from before
 * timelines): every beat shows the scene's first two characters, as the player
 * did before — prose written for an always-visible cast must not lose a
 * portrait just because a sentence refers to someone by a pronoun.
 */
function unstagedSections(segments: readonly SceneTextSegment[], roster: readonly Character[]): SceneSection[] {
  const cast = roster.slice(0, mvpLimits.maxSectionCharacters).map((c) => c.id);
  const taken = new Set<string>();
  return segments.map((segment) => {
    const id = nextSectionId(taken);
    taken.add(id);
    const section: SceneSection = { id, text: segment.text, characters: [...cast] };
    if (segment.break_before) section.break_before = segment.break_before;
    return section;
  });
}

/**
 * The timeline a reader sees for a scene: its stored `sections` when they
 * still spell out its `text`; the stored staging carried over onto a fresh
 * segmentation when the prose changed since; or, for a scene never staged,
 * its first two characters on every beat (see `stageUnstagedScenes` for
 * name-based staging). Characters not on the scene's roster are dropped and
 * each beat holds at most two. Empty for empty text.
 */
export function resolveSceneSections(
  scene: Scene,
  characters: readonly Character[],
  language: string
): SceneSection[] {
  const roster = sceneRoster(scene, characters);
  const rosterIds = new Set(roster.map((c) => c.id));
  const stored = scene.sections;
  if (stored && stored.length > 0 && sectionsMatchText(stored, scene.text)) {
    return stored.map((section) => sanitizeSection(section, rosterIds));
  }
  const segments = segmentSceneTextDetailed(scene.text, language);
  if (segments.length === 0) return [];
  if (stored && stored.length > 0) {
    return carrySectionsOver(stored, segments).map((section) => sanitizeSection(section, rosterIds));
  }
  return unstagedSections(segments, roster);
}

// ─── Carry-over between scenes ───────────────────────────────────────────────

/** For each scene, the scenes whose choices lead to it (in scene order). */
export function sceneParents(novel: Pick<Novel, "scenes">): Map<string, string[]> {
  const parents = new Map<string, string[]>();
  for (const scene of novel.scenes) {
    for (const choice of scene.choices) {
      const list = parents.get(choice.next_scene_id) ?? [];
      if (!list.includes(scene.id)) list.push(scene.id);
      parents.set(choice.next_scene_id, list);
    }
  }
  return parents;
}

/** Who is on stage at a scene's last beat. */
export function sceneEndCast(scene: Scene, characters: readonly Character[], language: string): string[] {
  const sections = resolveSceneSections(scene, characters, language);
  return [...(sections[sections.length - 1]?.characters ?? [])];
}

/**
 * Who walks into `scene` with the reader: the characters on stage when the
 * scene leading here ended who are on this scene's roster too — the hero who
 * goes from one location to the next stays on screen instead of vanishing
 * until the prose names them again. With several routes in, only those every
 * route ends with (the reader may come from any of them). Nobody arrives at a
 * scene no choice leads to (the start). `endCastOf` gives a parent's last-beat
 * cast (default: its resolved timeline).
 */
export function arrivingCast(
  novel: Pick<Novel, "scenes" | "characters" | "language">,
  scene: Scene,
  endCastOf: (parent: Scene) => readonly string[] = (parent) =>
    sceneEndCast(parent, novel.characters, novel.language),
  parents: ReadonlyMap<string, readonly string[]> = sceneParents(novel)
): string[] {
  const byId = new Map(novel.scenes.map((s) => [s.id, s]));
  const incoming = (parents.get(scene.id) ?? [])
    .filter((id) => id !== scene.id)
    .map((id) => byId.get(id))
    .filter((s): s is Scene => Boolean(s));
  if (incoming.length === 0) return [];
  const [first, ...rest] = incoming.map((parent) => endCastOf(parent));
  return first.filter((id) => scene.characters_present.includes(id) && rest.every((cast) => cast.includes(id)));
}

/** Scene ids in reading order: breadth-first from the start, then any the start does not reach. */
function readingOrder(novel: Pick<Novel, "scenes" | "start_scene_id">): string[] {
  const byId = new Map(novel.scenes.map((s) => [s.id, s]));
  const order: string[] = [];
  const seen = new Set<string>();
  const queue = byId.has(novel.start_scene_id) ? [novel.start_scene_id] : [];
  for (const id of queue) {
    if (seen.has(id)) continue;
    seen.add(id);
    order.push(id);
    for (const choice of byId.get(id)?.choices ?? []) {
      if (!seen.has(choice.next_scene_id) && byId.has(choice.next_scene_id)) queue.push(choice.next_scene_id);
    }
  }
  for (const scene of novel.scenes) if (!seen.has(scene.id)) order.push(scene.id);
  return order;
}

/**
 * Give every scene that has no timeline a name-based one (`autoStageSegments`:
 * a character steps on stage at the beat that names them), starting from who
 * arrives with the reader (`arrivingCast`) — scenes are staged in reading
 * order so a freshly staged scene hands its last beat's cast to the next. The
 * generation pipeline's fallback for scenes its staging model did not stage.
 * Returns the same novel reference when every scene already has sections.
 */
export function stageUnstagedScenes(novel: Novel): Novel {
  const staged = new Map<string, Scene>();
  const parents = sceneParents(novel);
  const endCastOf = (parent: Scene) =>
    sceneEndCast(staged.get(parent.id) ?? parent, novel.characters, novel.language);
  for (const id of readingOrder(novel)) {
    const scene = novel.scenes.find((s) => s.id === id);
    if (!scene || (scene.sections && scene.sections.length > 0)) continue;
    const segments = segmentSceneTextDetailed(scene.text, novel.language);
    if (segments.length === 0 || segments.length > mvpLimits.maxSectionsPerScene) continue;
    const arriving = arrivingCast(novel, scene, endCastOf, parents);
    staged.set(id, {
      ...scene,
      sections: autoStageSegments(segments, sceneRoster(scene, novel.characters), arriving)
    });
  }
  if (staged.size === 0) return novel;
  return { ...novel, scenes: novel.scenes.map((scene) => staged.get(scene.id) ?? scene) };
}

/**
 * A fresh name-based staging of ONE scene's current prose (drops any manual
 * staging): a character steps on stage at the beat that names them, and whoever
 * walks in with the reader (`arrivingCast`) is on stage from the first beat.
 * The editor's «Разложить заново» and the AI editor's restage both use it.
 * Returns undefined when the prose is empty or has more beats than a scene may
 * be staged with (the scene then shows its unstaged default).
 */
export function restageSceneSections(
  scene: Scene,
  novel: Pick<Novel, "scenes" | "characters" | "language">
): SceneSection[] | undefined {
  const segments = segmentSceneTextDetailed(scene.text, novel.language);
  if (segments.length === 0 || segments.length > mvpLimits.maxSectionsPerScene) return undefined;
  return autoStageSegments(segments, sceneRoster(scene, novel.characters), arrivingCast(novel, scene));
}

/**
 * Rebuild WHO IS ON STAGE in a scene from its (possibly rewritten) prose —
 * name-based, like `restageSceneSections` — without losing the author's other
 * layers: each new frame keeps the cues (sounds, special frames), location
 * override and expressions of the old frame it corresponds to (the same
 * sentence, else the one in the same position — `carrySectionsOver`). A cue
 * whose sentence is gone moves to the frame at the same relative position
 * rather than vanishing. `previous` is the scene's storyboard before the change.
 * Undefined when the prose is empty or too long to stage.
 */
export function restageKeepingLayers(
  scene: Scene,
  novel: Pick<Novel, "scenes" | "characters" | "language">,
  previous: readonly SceneSection[]
): SceneSection[] | undefined {
  const segments = segmentSceneTextDetailed(scene.text, novel.language);
  if (segments.length === 0 || segments.length > mvpLimits.maxSectionsPerScene) return undefined;
  const staged = autoStageSegments(segments, sceneRoster(scene, novel.characters), arrivingCast(novel, scene));
  if (previous.length === 0) return staged;
  const carried = carrySectionsOver(previous, segments);
  const result = staged.map((fresh, index) => {
    const old = carried[index];
    const section: SceneSection = { ...fresh };
    if (old?.cues && old.cues.length > 0) section.cues = [...old.cues];
    if (old?.background_asset_id) section.background_asset_id = old.background_asset_id;
    if (old?.emotions) {
      const kept = Object.fromEntries(
        Object.entries(old.emotions).filter(([id]) => fresh.characters.includes(id))
      );
      if (Object.keys(kept).length > 0) section.emotions = kept;
    }
    return section;
  });
  // Cues of old frames no new frame took over land at the same relative spot.
  const placed = new Set(result.flatMap((section) => (section.cues ?? []).map((cue) => cue.id)));
  previous.forEach((old, oldIndex) => {
    for (const cue of old.cues ?? []) {
      if (placed.has(cue.id)) continue;
      const target = result[Math.min(result.length - 1, Math.round((oldIndex / Math.max(1, previous.length)) * result.length))];
      const cues = target.cues ?? [];
      const clash = cue.kind === "frame" && cues.some((c) => c.kind === "frame");
      if (clash || cues.length >= mvpLimits.maxSectionCues) continue;
      target.cues = [...cues, cue];
      placed.add(cue.id);
    }
  });
  return result;
}

/**
 * Drop section references that no longer resolve: on-stage characters who left
 * the scene's roster (and their emotions), a location or cue asset that was
 * deleted. Run after any edit that removes characters or assets, so the novel
 * stays valid (validation rejects dangling section refs). Returns the same
 * novel reference when nothing needed pruning.
 */
export function pruneSectionRefs(novel: Novel): Novel {
  const assetById = new Map(novel.assets.map((a) => [a.id, a]));
  let changed = false;
  const scenes = novel.scenes.map((scene) => {
    if (!scene.sections) return scene;
    const roster = new Set(scene.characters_present);
    let sceneChanged = false;
    const sections = scene.sections.map((section) => {
      let next = sanitizeSection(section, roster);
      if (next.background_asset_id && assetById.get(next.background_asset_id)?.type !== "background") {
        next = { ...next };
        delete next.background_asset_id;
      }
      if (next.cues) {
        const cues = next.cues.filter((cue) => assetById.has(cue.asset_id));
        if (cues.length !== next.cues.length) next = { ...next, cues };
      }
      if (next !== section) sceneChanged = true;
      return next;
    });
    if (!sceneChanged) return scene;
    changed = true;
    return { ...scene, sections };
  });
  return changed ? { ...novel, scenes } : novel;
}

/**
 * Which section's staging a translated reading beat uses. A translation is
 * segmented on its own, so its beat count can differ from the original's; the
 * beats are then mapped proportionally (same count → one-to-one).
 */
export function sectionIndexForBeat(beat: number, beatCount: number, sectionCount: number): number {
  if (sectionCount <= 0) return -1;
  if (beatCount === sectionCount) return Math.min(beat, sectionCount - 1);
  return Math.min(sectionCount - 1, Math.floor((beat * sectionCount) / Math.max(beatCount, 1)));
}

/** The location a section shows: its own override, else the scene's. */
export function sectionBackgroundId(scene: Pick<Scene, "background_asset_id">, section: SceneSection | undefined): string {
  return section?.background_asset_id || scene.background_asset_id;
}
